// ────────────────────────────────────────────────────────────────
// ConfirmReplaceDialog.jsx  – generic duplicate-name prompt
// Re-usable dialog for handling duplicate asset uploads
// ----------------------------------------------------------------
// UX notes
// • Accessible: titles, descriptions, focus management
// • Keyboard friendly: ⏎ defaults to primary action, ⎋ cancels
// • Theming: relies on MUI v5 palette and typography
// • Flexible: parent decides what to do in each branch
// • Cancel via backdrop-click or Esc (no explicit button)
// ----------------------------------------------------------------
import React, { forwardRef } from 'react';
import PropTypes            from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Slide,
  Typography,
  Box,
  useTheme,
} from '@mui/material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';

// Small helper for slide-up transition
const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/**
 * ConfirmReplaceDialog
 * ------------------------------------------------------------
 * @param {boolean}  open        – whether the dialog is visible
 * @param {string}   fileName    – name of the duplicate file
 * @param {Function} onReplace   – called when user chooses “+ New Version”
 * @param {Function} onCopy      – called when user chooses “Duplicate Copy”
 * @param {Function} onClose     – always called when dialog closes
 */
const ConfirmReplaceDialog = ({
  open,
  fileName,
  onReplace,
  onCopy,
  onClose,
}) => {
  const theme = useTheme();

  /* ——— handlers ——— */
  const handleReplace = () => {
    onReplace?.();
    onClose?.();
  };

  const handleCopy = () => {
    onCopy?.();
    onClose?.();
  };

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted                                           /* keep for faster reopen */
      onClose={onClose}                                     /* backdrop / Esc */
      aria-labelledby="duplicate-file-dialog-title"
      aria-describedby="duplicate-file-dialog-description"
      maxWidth="xs"
      fullWidth
    >
      {/* ——— title ——— */}
      <DialogTitle id="duplicate-file-dialog-title">
        Duplicate asset name
      </DialogTitle>

      {/* ——— body ——— */}
      <DialogContent dividers>
        <Box display="flex" alignItems="center" gap={2}>
          <InsertDriveFileIcon
            fontSize="large"
            sx={{ color: theme.palette.text.secondary }}
          />
          <Box>
            <Typography variant="subtitle1" fontWeight={500} noWrap>
              {fileName}
            </Typography>
            <DialogContentText id="duplicate-file-dialog-description">
              An asset with the same name already exists in your library.
              Choose whether to create a duplicate copy or add a new version.
            </DialogContentText>
          </Box>
        </Box>
      </DialogContent>

      {/* ——— actions ——— */}
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="outlined" onClick={handleCopy}>
          Duplicate&nbsp;Copy
        </Button>
        <Button
          variant="contained"
          onClick={handleReplace}
          autoFocus                                        /* ⏎ triggers this */
        >
          +&nbsp;New&nbsp;Version
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ConfirmReplaceDialog.propTypes = {
  open      : PropTypes.bool.isRequired,
  fileName  : PropTypes.string.isRequired,
  onReplace : PropTypes.func,
  onCopy    : PropTypes.func,
  onClose   : PropTypes.func.isRequired,
};

export default ConfirmReplaceDialog;
